import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Crown, LogOut, Plus, Search, Settings2, Users } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ErrorState } from "@/components/kit";
import { inviteUrl } from "@/components/layout/nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";

function ServerCard({ g, clientId }) {
  const missing = g.bot_installed === false;
  const canInvite = missing && clientId && g.discord_id;
  return (
    <div data-testid={`guild-card-${g.id}`} className={`group relative rounded-2xl border bg-zinc-900 overflow-hidden transition-colors ${missing ? "border-zinc-800" : "border-zinc-800 hover:border-volt/50"}`}>
      <div className="h-20 relative overflow-hidden">
        <img src={g.icon} alt="" className="absolute inset-0 w-full h-full object-cover scale-150 blur-2xl opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-zinc-900" />
      </div>
      <div className="px-5 pb-5 -mt-10 relative">
        <img src={g.icon} alt="" className={`w-16 h-16 rounded-2xl bg-zinc-800 border-4 border-zinc-900 ${missing ? "grayscale opacity-60" : ""}`} />
        <div className="mt-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-head font-semibold text-lg truncate">{g.name}</div>
            <div className="text-sm text-zinc-500 flex items-center gap-1.5 mt-0.5"><Users className="w-3.5 h-3.5" /> {(g.member_count || 0).toLocaleString()} members</div>
          </div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shrink-0 ${g.plan === "free" ? "bg-zinc-800 text-zinc-400" : "bg-volt/15 text-volt"}`}>{g.plan}</span>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap min-h-[22px]">
          {g.bot_installed === true && <span data-testid="guild-installed-badge" className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">Bot installed</span>}
          {missing && <span data-testid="guild-missing-badge" className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">Bot not in server</span>}
          {!g.is_owner && g.staff_role && <span data-testid="guild-staff-badge" className="text-[11px] px-2 py-0.5 rounded-full bg-[#f59e0b]/10 text-[#f59e0b] border border-[#f59e0b]/30">Staff · {g.staff_role}</span>}
        </div>
        <div className="mt-4 flex gap-2">
          {canInvite ? (<>
            <a href={inviteUrl(clientId, g.discord_id)} target="_blank" rel="noopener noreferrer" data-testid={`guild-invite-${g.id}`}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-volt text-volt-ink font-semibold text-sm py-2.5 hover:bg-volt-hover transition-colors"><Plus className="w-4 h-4" /> Invite bot</a>
            <Link to={`/app/g/${g.id}`} data-testid={`guild-${g.id}`} title="Configure anyway" aria-label="Configure anyway"
              className="w-11 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"><Settings2 className="w-4 h-4" /></Link>
          </>) : (
            <Link to={`/app/g/${g.id}`} data-testid={`guild-${g.id}`}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-zinc-700 text-sm font-semibold py-2.5 group-hover:bg-volt group-hover:text-volt-ink group-hover:border-volt transition-colors">
              Manage server <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GuildSelect() {
  const [guilds, setGuilds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [clientId, setClientId] = useState("");
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const load = () => {
    setLoading(true); setError(false);
    api.get("/guilds").then((r) => setGuilds(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/auth/discord/config").then((r) => setClientId(r.data?.client_id || "")).catch(() => {});
  }, []);
  const list = useMemo(() => guilds.filter((g) => g.name.toLowerCase().includes(q.trim().toLowerCase())), [guilds, q]);

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-zinc-800 sticky top-0 z-40 bg-black/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 font-head font-black text-lg"><BrandLogo className="w-7 h-7" /> crank<span className="text-volt">.live</span></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {(user?.role === "admin" || user?.staff_role) && <Link to="/admin" data-testid="owner-console-link" className="text-sm text-volt hover:text-volt-hover flex items-center gap-1.5 font-medium"><Crown className="w-4 h-4" /> <span className="hidden sm:inline">Owner Console</span></Link>}
            <NotificationBell />
            <ThemeToggle />
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
      </header>
      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-72 hero-glow pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-volt mb-2">Server dashboard</div>
              <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Welcome back, {user?.name}</h1>
              <p className="text-zinc-400 mt-2">Pick a server to manage, or invite Crank.live to a new one.</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-500" />
              <input data-testid="guild-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search servers…"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2.5 text-sm outline-none focus:border-volt transition-colors" />
            </div>
          </div>
          {error ? <ErrorState title="Couldn't load your servers" desc="Check your connection and try again." onRetry={load} />
            : loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{[0, 1, 2].map((i) => <div key={i} className="h-60 rounded-2xl bg-zinc-900 border border-zinc-800 animate-pulse" />)}</div>
            ) : !guilds.length ? (
              <div data-testid="guilds-empty" className="text-center border border-dashed border-zinc-800 rounded-2xl py-16 px-6">
                <div className="font-head text-xl font-semibold">No servers yet</div>
                <p className="text-zinc-500 mt-1 text-sm">You need the Manage Server permission on a Discord server to manage it here.</p>
                {clientId && <a href={inviteUrl(clientId)} target="_blank" rel="noopener noreferrer" data-testid="invite-bot-cta" className="inline-flex items-center gap-2 mt-5 rounded-lg bg-volt text-volt-ink font-semibold text-sm px-5 py-2.5 hover:bg-volt-hover"><Plus className="w-4 h-4" /> Add Crank.live to Discord</a>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((g) => <ServerCard key={g.id} g={g} clientId={clientId} />)}
                {clientId && (
                  <a href={inviteUrl(clientId)} target="_blank" rel="noopener noreferrer" data-testid="invite-bot-cta"
                    className="rounded-2xl border border-dashed border-zinc-800 flex flex-col items-center justify-center gap-2 min-h-[15rem] text-zinc-500 hover:text-volt hover:border-volt/50 transition-colors">
                    <span className="w-12 h-12 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center"><Plus className="w-5 h-5" /></span>
                    <span className="font-medium text-sm">Add to another server</span>
                  </a>
                )}
                {!list.length && <p className="text-zinc-500 text-sm">No servers match “{q}”.</p>}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
